/**
 * Campaign Gallery Page - Photo gallery
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ArrowLeft, Image, X, ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CampaignGallery = () => {
  const { slug } = useParams();
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [filter, setFilter] = useState('all');

  const { data: campaign } = useQuery({
    queryKey: ['campaign', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/campaigns/${slug}`);
      return res.json();
    }
  });

  const { data: gallery = [], isLoading } = useQuery({
    queryKey: ['campaign-gallery', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/campaigns/${slug}/gallery`);
      return res.json();
    }
  });

  const categories = ['all', ...new Set(gallery.map(img => img.category).filter(Boolean))];
  const filteredGallery = filter === 'all' ? gallery : gallery.filter(img => img.category === filter);

  const openLightbox = (index) => setSelectedIndex(index);
  const closeLightbox = () => setSelectedIndex(null);
  const prevImage = () => setSelectedIndex(prev => prev > 0 ? prev - 1 : filteredGallery.length - 1);
  const nextImage = () => setSelectedIndex(prev => prev < filteredGallery.length - 1 ? prev + 1 : 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to={`/campaigns/${slug}`}
            className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Campaign
          </Link>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Image className="h-8 w-8 text-emerald-600" />
            Photo Gallery
          </h1>
          {campaign && (
            <p className="text-slate-500 mt-1">{campaign.name}</p>
          )}
        </div>

        {/* Category Filter */}
        {categories.length > 1 && (
          <div className="flex gap-2 mb-6 flex-wrap">
            {categories.map(cat => (
              <Button
                key={cat}
                variant={filter === cat ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(cat)}
                className={filter === cat ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </Button>
            ))}
          </div>
        )}

        {/* Gallery Grid */}
        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <Skeleton key={i} className="aspect-square rounded-lg" />
            ))}
          </div>
        ) : filteredGallery.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <Image className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>No photos in the gallery yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredGallery.map((img, index) => (
              <div 
                key={img.id}
                className="group relative aspect-square overflow-hidden rounded-lg cursor-pointer"
                onClick={() => openLightbox(index)}
              >
                <img 
                  src={img.image_url}
                  alt={img.title}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-white text-sm font-medium truncate">{img.title}</p>
                    {img.category && (
                      <Badge variant="secondary" className="mt-1 text-xs">{img.category}</Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Lightbox */}
        <Dialog open={selectedIndex !== null} onOpenChange={closeLightbox}>
          <DialogContent className="max-w-4xl p-0 bg-black border-none">
            {selectedIndex !== null && filteredGallery[selectedIndex] && (
              <div className="relative">
                <button 
                  onClick={closeLightbox}
                  className="absolute top-4 right-4 z-10 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                >
                  <X className="h-5 w-5" />
                </button>
                
                <img 
                  src={filteredGallery[selectedIndex].image_url}
                  alt={filteredGallery[selectedIndex].title}
                  className="w-full max-h-[80vh] object-contain"
                />
                
                {/* Navigation */}
                <button 
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>

                {/* Caption */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                  <h3 className="text-white font-medium">{filteredGallery[selectedIndex].title}</h3>
                  {filteredGallery[selectedIndex].description && (
                    <p className="text-white/80 text-sm mt-1">{filteredGallery[selectedIndex].description}</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CampaignGallery;
